import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { callAPI } from "../helper/apiUtils";
import { BASE_URL, TOGGLE_DRAWER } from "../helper/constants";
import { $user } from "../helper/UserFactory";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { DE } from "../Languages/DE";
import { EN } from "../Languages/EN";
import { KJUUP_COLORS } from "../helper/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  TOGGLE_CHAT,
  TOGGLE_LATEST_MSG,
  TOGGLE_PROJECT,
  OPEN_PROJECTS,
  OPEN_CHATS,
  SET_IN_ACTIVITY,
  TOGGLE_WORK_AREA,
  GROUPS,
  GROUP_CHATS,
  SET_DEEPLINK_DATA,
  SET_DEEPLINK_LOADING,
  SET_NEW_GROUP_DATA,
  FROM_FROROIEGN_PROFILE,
  SET_OPEN_SIDE_BAR,
  RIGHT_TOP,
  UPDATE_PROJECT_ACTION_FIRE,
  SET_TAB,
  SET_ACTIVITY_MESSAGES,
  SET_ACTIVITY_MESSAGES_LAST_FETCHED,
} from "../store";
import { FiSearch } from "react-icons/fi";
import { Oval } from "react-loader-spinner";
import ChatGroupSelection from "./ChatGroupSelection";
import RoomSelection from "./RoomSelection";
import CompanyListDialog from "../pages/Project/Dialogs/CompanyListDialog";
import { useHistory } from "react-router-dom";
import {
  ContactSourceTabs,
  TeamsContactsPanel,
  attachTeamsCopy,
  notifyTeamsTooMany,
  startTeamsOnlyChat,
  teamsPickerText,
  useTeamsAccounts,
  useTeamsPeopleSearch,
} from "./chat/TeamsContacts";
import { classifyChatSelection, mergeTeamsPeople, teamsPickerState, toggleTeamsPerson } from "../utils/teams/combinedChat";
import "./MemberSelection.css";

/**
 * New chat from Messages: pick Kjuup contacts and, with a writable Microsoft Teams account
 * (`teamsEnabled`), Teams contacts in a second tab (one search field for both).
 *   Kjuup only  the Kjuup flow (direct message or group with company / project / name)
 *   Teams only  a Teams chat (POST /teams/chats) → onTeamsChatCreated({ groupId, chatType, name, project })
 *   both        a Kjuup group through the same steps, then its Teams copy (POST /teams/bridges)
 */
function MemberSelection({ onClose, onComplete, teamsEnabled = false, onTeamsChatCreated }) {
  console.log("=== MemberSelection COMPONENT LOADED (v2) ===");
  const { isGerm, strings } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const activityMessages = useSelector((state) => state.activityMessages);
  const currentUser = $user.get();
  const history = useHistory();

  // Pagination and search state
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  // Track which members have their company connection list expanded.
  const [expandedCompanies, setExpandedCompanies] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(50);
  const searchTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isInitialMount = useRef(true);
  const [inputFocused, setInputFocused] = useState(false);

  // Add new member form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [openCompanies, setOpenCompanies] = useState(new Set());
  const companyGroupsInitialized = useRef(false);


  // Multi-step flow state
  const [currentStep, setCurrentStep] = useState("members"); // "members" | "chatGroup" | "room"
  const [selectedChatGroup, setSelectedChatGroup] = useState(null);
  const [showInternalChatModal, setShowInternalChatModal] = useState(false);
  const [kjuupCompanies, setKjuupCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [showDirectMessageModal, setShowDirectMessageModal] = useState(false);
  const [directMessageRefused, setDirectMessageRefused] = useState(false); // Track if user refused direct message

  // Microsoft Teams contacts (second tab): the picked people, the visible tab and the Teams-only
  // "start chat" dialog (group name for several people).
  const [contactTab, setContactTab] = useState("kjuup");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [showTeamsChatModal, setShowTeamsChatModal] = useState(false);
  const [teamsTopic, setTeamsTopic] = useState("");
  const [teamsBusy, setTeamsBusy] = useState(false);
  const teamsAccountsState = useTeamsAccounts(!!teamsEnabled);
  const teamsAccounts = teamsAccountsState.accounts;
  const teamsAccountId = teamsAccountsState.accountId;
  const teamsPicker = teamsPickerState({ enabled: teamsEnabled, accounts: teamsAccounts, selected: selectedTeams });
  const teamsTabOn = teamsPicker.tabOn;
  const pickedTeams = teamsPicker.picked;
  const teamsSearch = useTeamsPeopleSearch({ enabled: teamsTabOn, query: searchValue, accountId: teamsAccountId });
  const selectionKind = classifyChatSelection(selectedMembers, pickedTeams);
  const teamsText = (key, de, en) => teamsPickerText(strings, isGerm, key, de, en);

  // Auto-focus input on mount with delay for modal animation
  useEffect(() => {
    const focusTimeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 200);
    return () => clearTimeout(focusTimeout);
  }, []);

  // Initial load on mount
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoadingMembers(true);
        const query = encodeURIComponent("");
        const response = await callAPI(
          `/user/allUsersSearch?limit=${limit}&page=1&query=${query}&forChat=1`,
          {},
          "GET"
        );

        const data = response.data || response;
        console.log("MemberSelection - Initial API Response:", data);

        if (data && data.status && data.data && Array.isArray(data.data.users)) {
          const formattedMembers = data.data.users
            .filter((user) => user.id !== currentUser?.id)
            .map((user) => ({
              id: user.id,
              name: user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim(),
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              avatar: user.img,
              fullName: user.name,
              company: user.company,
              companyName: user.companyName,
              companies: user.companies,
              isKjuup: user.isKjuup,
            }));

          console.log("MemberSelection - Initial formatted members:", formattedMembers.length);
          setMembers(formattedMembers);
          setHasMore(formattedMembers.length === limit);

          if (data.data.companyUsers) {
            const seen = new Set();
            const deduped = data.data.companyUsers
              .map((cg) => ({
                ...cg,
                users: (cg.users || []).filter((u) => {
                  if (!u?.id || seen.has(u.id)) return false;
                  seen.add(u.id);
                  return true;
                }),
              }))
              .filter((cg) => cg.users.length > 0);
            setCompanyGroups(deduped);
            if (!companyGroupsInitialized.current) {
              companyGroupsInitialized.current = true;
              if (deduped.length === 1) {
                setOpenCompanies(new Set([deduped[0].companyId]));
              }
            }
          }
        } else {
          console.warn("MemberSelection - Invalid initial response:", data);
          setMembers([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error in initial load:", error);
        $crud.notify({
          type: "error",
          message: strings.error_loading_members,
        });
        setMembers([]);
        setHasMore(false);
      } finally {
        setLoadingMembers(false);
      }
    };

    initialLoad();
  }, []);

  // Handle search - trigger on 1st character or when cleared
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Initial empty load is handled by the mount effect above — skip duplicate call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If search is empty, load immediately
    if (!searchValue.trim()) {
      setDebouncedSearch("");
      setPage(1);
      setMembers([]);
      setHasMore(true);
      loadMembers(1, "");
      return;
    }

    // If search has 1+ characters, trigger search immediately
    if (searchValue.trim().length >= 1) {
      setDebouncedSearch(searchValue);
      setPage(1);
      setMembers([]);
      setHasMore(true);
    }
  }, [searchValue]);

  // Load members when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== undefined && debouncedSearch !== "") {
      loadMembers(1, debouncedSearch);
    }
  }, [debouncedSearch]);

  // Load more when page changes (for pagination)
  useEffect(() => {
    if (page > 1) {
      loadMembers(page, debouncedSearch || "", true);
    }
  }, [page]);

  // Load members from API with pagination and search
  const loadMembers = async (pageNum = 1, searchQuery = "", append = false) => {
    if (append && (loadingMore || loadingMembers)) return;
    if (!append && loadingMembers && members.length > 0 && pageNum === 1) return; // Only prevent if we already have data and it's not pagination

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingMembers(true);
      }

      const query = encodeURIComponent(searchQuery || "");
      const response = await callAPI(
        `/user/allUsersSearch?limit=${limit}&page=${pageNum}&query=${query}&forChat=1`,
        {},
        "GET"
      );

      const data = response.data || response;
      console.log("MemberSelection - API Response:", data);
      // Log first user to see all available fields
      if (data?.data?.users?.[0]) {
        console.log("MemberSelection - Sample user from API:", data.data.users[0]);
      }

      if (data && data.status && data.data && Array.isArray(data.data.users)) {
        // Filter out current user and format members
        const formattedMembers = data.data.users
          .filter((user) => user.id !== currentUser?.id)
          .map((user) => ({
            id: user.id,
            name: user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim(),
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            avatar: user.img,
            fullName: user.name,
            company: user.company,
            companyName: user.companyName,
            companies: user.companies,
            isKjuup: user.isKjuup,
          }));

        console.log("MemberSelection - Formatted members:", formattedMembers.length);

        if (append) {
          setMembers((prev) => [...prev, ...formattedMembers]);
        } else {
          setMembers(formattedMembers);
        }

        if (data.data.companyUsers && !append) {
          const seen = new Set();
          const deduped = data.data.companyUsers
            .map((cg) => ({
              ...cg,
              users: (cg.users || []).filter((u) => {
                if (!u?.id || seen.has(u.id)) return false;
                seen.add(u.id);
                return true;
              }),
            }))
            .filter((cg) => cg.users.length > 0);
          setCompanyGroups(deduped);
        }

        // Check if there are more pages
        setHasMore(formattedMembers.length === limit);
      } else {
        console.warn("MemberSelection - Invalid response or no users:", data);
        if (!append) {
          setMembers([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading members:", error);
      console.error("Error details:", error.response || error.message);
      if (!append) {
        $crud.notify({
          type: "error",
          message: strings.error_loading_members,
        });
        setMembers([]);
      }
      setHasMore(false);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoadingMembers(false);
      }
    }
  };

  // Handle scroll for infinite pagination
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (contactTab !== "kjuup") return;
    if (isNearBottom && hasMore && !loadingMore && !loadingMembers && debouncedSearch !== undefined) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, loadingMore, loadingMembers, debouncedSearch, contactTab]);

  // Toggle expand/collapse of a member's company connection list
  const toggleCompanyExpanded = (memberId) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Pick / unpick a Teams person (TEAMS_MEMBERS_MAX per chat).
  const toggleTeamsSelection = (person) => {
    const r = toggleTeamsPerson(selectedTeams, person);
    if (r.error === "too_many") {
      notifyTeamsTooMany(strings, isGerm);
      return;
    }
    setSelectedTeams(r.list);
  };

  // People belong to one Microsoft organisation: another account starts the Teams pick over.
  const pickTeamsAccount = (id) => {
    if (teamsBusy || id === teamsAccountId) return;
    teamsAccountsState.setAccountId(id);
    setSelectedTeams([]);
  };

  // Teams contacts only: the Teams chat (1 person = 1:1, more = group with the optional name).
  const startTeamsChat = async () => {
    if (teamsBusy || !pickedTeams.length) return;
    setTeamsBusy(true);
    const created = await startTeamsOnlyChat({
      people: pickedTeams,
      topic: teamsTopic,
      accountId: teamsAccountId,
      strings,
      isGerm,
    });
    setTeamsBusy(false);
    if (!created) return;
    setShowTeamsChatModal(false);
    if (onTeamsChatCreated) onTeamsChatCreated(created);
    if (onComplete) onComplete();
    if (onClose) onClose();
  };

  // Onboard new user
  const onboardNewMember = async () => {
    if (!newMember.firstName.trim() || !newMember.lastName.trim() || !newMember.email.trim()) {
      $crud.notify({
        type: "error",
        message: strings.please_fill_all_fields,
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMember.email)) {
      $crud.notify({
        type: "error",
        message: strings.invalid_email,
      });
      return;
    }

    try {
      setOnboardingLoading(true);
      const { data } = await callAPI(
        `/user/onboardUser`,
        {},
        "POST",
        {
          firstName: newMember.firstName.trim(),
          lastName: newMember.lastName.trim(),
          email: newMember.email.trim().toLowerCase(),
        }
      );

      if (data.status) {
        // Add newly onboarded user to members list
        const newUser = {
          id: data.user?.id,
          name: `${newMember.firstName} ${newMember.lastName}`,
          firstName: newMember.firstName.trim(),
          lastName: newMember.lastName.trim(),
          email: newMember.email.trim().toLowerCase(),
          avatar: data.user?.img,
          fullName: `${newMember.firstName} ${newMember.lastName}`,
        };

        setMembers((prev) => [newUser, ...prev]);

        // Auto-select the newly added user
        setSelectedMembers((prev) => [...prev, newUser.id]);

        // Reset form
        setNewMember({ firstName: "", lastName: "", email: "" });
        setShowAddForm(false);

        $crud.notify({
          type: "success",
          message: strings.member_added_successfully,
        });
      } else {
        $crud.notify({
          type: "error",
          message: data.message || (strings.error_onboarding_user),
        });
      }
    } catch (error) {
      console.error("Error onboarding user:", error);
      $crud.notify({
        type: "error",
        message: strings.error_onboarding_user,
      });
    } finally {
      setOnboardingLoading(false);
    }
  };

  // Create one-to-one chat
  const createDirectChat = async (member, company = null) => {
    try {
      setLoading(true);
      
      // Build the API payload
      const payload = {
        groupType: "user",
        isOneToOne: true,
        users: [currentUser?.id, member.id],
      };
      
      // Add companyId for internal chats
      if (company && company.id) {
        payload.companyId = company.id;
      }
      
      const { data } = await callAPI(
        `/group`,
        {},
        "POST",
        payload
      );

      if (data.status && data.chat) {
        const chatData = data.chat;
        const memberName = member.name || member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim();
        
        // Ensure the group carries a well-formed users array with both
        // participants so the chat list can resolve the partner (e.g. for the
        // Kjuup+ green outline) immediately, without waiting for a refetch.
        const chatUsers = (Array.isArray(chatData.users) && chatData.users.some((u) => u?.id === member.id))
          ? chatData.users
          : [
              { id: currentUser?.id },
              { id: member.id, fullName: memberName, avatar: member.avatar || null, isKjuup: member.isKjuup },
            ];

        // Build chat entry for the activity messages list (same structure as AllChats items)
        const activityMessageEntry = {
          id: chatData.id,
          mainId: chatData.id,
          group: {
            ...chatData,
            id: chatData.id,
            name: memberName,
            isOneToOne: true,
            isInternal: Boolean(chatData.isInternal),
            users: chatUsers,
          },
          senderId: member.id,
          senderName: memberName,
          senderAvatar: member.avatar || null,
          name: memberName,
          avatar: member.avatar || null,
          isOpened: true,
          isOneToOne: true,
          unseenChatCount: 0,
          createdAt: new Date().toISOString(),
        };
        
        // Build chat payload for openChats (for right pane display)
        const chatPayload = {
          ...chatData,
          id: chatData.id,
          key: chatData.id,
          name: memberName,
          isOpened: true,
          isOneToOne: true,
          avatar: member.avatar || null,
          defaultChatGroup: Boolean(chatData.defaultChatGroup),
          isInternal: Boolean(chatData.isInternal),
          onlyView: false,
          unseenChatCount: 0,
        };
        
        // Close the dialog first
        if (onComplete) onComplete();
        if (onClose) onClose();
        
        // Set up state BEFORE navigation to avoid race conditions
        // Clear projects and groups first
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: GROUPS, groups: [] });
        dispatch({ type: GROUP_CHATS, groupChats: [] });
        
        // Set up chat view states for inline display in LatestMessages
        dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: SET_IN_ACTIVITY, status: true });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        dispatch({ type: TOGGLE_CHAT, payload: false });
        dispatch({ type: SET_OPEN_SIDE_BAR, payload: "LatestMessages" });
        dispatch({ type: RIGHT_TOP, rightTop: "" });
        
        // Update activity messages list: close all other chats, add new one at top as opened
        const currentMessages = activityMessages || [];
        const updatedMessages = currentMessages
          .filter(msg => msg.group?.id !== chatData.id) // Remove if already exists
          .map(msg => ({ ...msg, isOpened: false })); // Close all others
        const newMessages = [activityMessageEntry, ...updatedMessages];
        dispatch({ type: SET_ACTIVITY_MESSAGES, messages: newMessages });
        // Mark cache as fresh to prevent LatestMessages from immediately refetching
        dispatch({ type: SET_ACTIVITY_MESSAGES_LAST_FETCHED, payload: Date.now() });
        
        // Set the chat data for right pane display
        dispatch({ type: OPEN_CHATS, openChats: [chatPayload] });
        
        // Navigate to LatestMessages page AFTER state is set up
        history.replace("/messages");
      } else {
        // Clear loading on error
        dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
        $crud.notify({
          type: "error",
          message: data.message || (strings.error_creating_chat),
        });
      }
    } catch (error) {
      console.error("Error creating direct chat:", error);
      // Clear loading on error
      dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
      $crud.notify({
        type: "error",
        message: strings.error_creating_chat,
      });
    } finally {
      setLoading(false);
    }
  };

  // Create group chat
  const createGroupChat = async (userIds, groupName = null) => {
    try {
      setLoading(true);

      // If no group name provided, generate one
      const finalGroupName = groupName ||
        (strings.new_group) + " " + new Date().toLocaleDateString();

      const { data } = await callAPI(
        `/group`,
        {},
        "POST",
        {
          groupType: "user",
          isOneToOne: false,
          name: finalGroupName,
          users: [currentUser?.id, ...userIds],
        }
      );

      if (data.status) {
        // Kjuup + Teams: the group is kept and opened even when its Teams copy fails (toast).
        if (selectionKind === "combined") {
          await attachTeamsCopy({ groupId: data.chat.id, people: pickedTeams, accountId: teamsAccountId, strings, isGerm });
        }
        // Navigate to group chat (this is external - from createDirectChat without company)
        dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: false });
        dispatch({ type: TOGGLE_CHAT, payload: false });
        dispatch({ type: SET_DEEPLINK_DATA, payload: { chatGroupId: data.chat.id } });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: SET_IN_ACTIVITY, status: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        dispatch({ type: GROUPS, groups: [] });

        // Use requestAnimationFrame for immediate execution instead of setTimeout
        requestAnimationFrame(() => {
          dispatch({ type: FROM_FROROIEGN_PROFILE, payload: "pageLoad" });
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: "chat1" });
          dispatch({ type: RIGHT_TOP, rightTop: "chat1" });
        });

        if (onComplete) onComplete();
        if (onClose) onClose();
      } else {
        $crud.notify({
          type: "error",
          message: data.message || (strings.error_creating_group),
        });
      }
    } catch (error) {
      console.error("Error creating group chat:", error);
      $crud.notify({
        type: "error",
        message: strings.error_creating_group,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle continue button click
  const handleContinue = async () => {
    console.log("handleContinue CALLED - selectedMembers:", selectedMembers.length, selectedMembers);

    // Teams contacts only: a Teams chat, no Kjuup steps.
    if (selectionKind === "teams") {
      setShowTeamsChatModal(true);
      return;
    }

    // Kjuup + Teams with one Kjuup contact: always a group (never the direct-message path).
    if (selectionKind === "combined" && selectedMembers.length === 1) {
      await handleDirectMessageCancel();
      return;
    }
    
    // If no members selected, show internal/external popup and call suggested APIs
    if (selectedMembers.length === 0) {
      console.log("handleContinue - No members selected, showing internal/external modal");
      setShowInternalChatModal(true);
      return;
    }

    // If exactly one member is selected, show direct message confirmation popup first
    if (selectedMembers.length === 1) {
      console.log("handleContinue - Single member, showing direct message modal");
      setShowDirectMessageModal(true);
      return;
    }

    console.log("handleContinue - Multiple members, calling getSuggestedKjuupCompany API");
    try {
      setLoading(true);
      // Call new API to get Kjuup Plus companies for selected users
      const { data } = await callAPI(
        `/company/getSuggestedKjuupCompany`,
        {},
        "POST",
        {
          users: selectedMembers,
        }
      );

      console.log("handleContinue - getSuggestedKjuupCompany response:", data);
      if (data.status && data.companies && data.companies.length > 0) {
        // Show internal chat popup
        console.log("handleContinue - Found", data.companies.length, "Kjuup+ companies:", data.companies);
        setKjuupCompanies(data.companies);
        setShowInternalChatModal(true);
      } else {
        // No Kjuup Plus companies found, proceed to regular chat group selection
        setCurrentStep("chatGroup");
      }
    } catch (error) {
      console.error("Error fetching Kjuup companies:", error);
      // On error, proceed to regular chat group selection
      setCurrentStep("chatGroup");
    } finally {
      setLoading(false);
    }
  };

  // Handle direct message confirmation - directly create external direct chat
  const handleDirectMessageConfirm = async () => {
    setShowDirectMessageModal(false);
    if (selectedMembers.length === 1) {
      const selectedMember = members.find(m => m.id === selectedMembers[0]);
      if (selectedMember) {
        // User confirmed direct message - create external direct chat directly
        // (internal/external popup only shows when user cancels direct message to create group chat)
        dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: false });
        await createDirectChat(selectedMember, null);
      }
    }
  };

  // Handle direct message cancel - check for internal/external chat options
  const handleDirectMessageCancel = async () => {
    console.log("handleDirectMessageCancel CALLED");
    setShowDirectMessageModal(false);
    setDirectMessageRefused(true); // Mark that user refused direct message
    
    // Check if the selected user shares a Kjuup+ company with current user
    // to show the internal/external chat selection popup for group chat
    if (selectedMembers.length === 1) {
      const selectedMember = members.find(m => m.id === selectedMembers[0]);
      if (selectedMember) {
        console.log("handleDirectMessageCancel - Calling getSuggestedKjuupCompany for member:", selectedMember.id);
        try {
          setLoading(true);
          const { data } = await callAPI(
            `/company/getSuggestedKjuupCompany`,
            {},
            "POST",
            {
              users: [selectedMember.id],
            }
          );

          console.log("handleDirectMessageCancel - API response:", data);
          setLoading(false); // Clear loading before showing modal
          
          if (data.status && data.companies && data.companies.length > 0) {
            // Show internal chat popup to let user choose between internal/external group chat
            console.log("handleDirectMessageCancel - Found", data.companies.length, "companies, showing internal modal");
            setKjuupCompanies(data.companies);
            setShowInternalChatModal(true);
            return;
          }
        } catch (error) {
          console.error("Error fetching Kjuup companies:", error);
          setLoading(false);
          // On error, proceed to regular chat group selection
        }
      }
    }
    
    // No Kjuup+ companies found or error, proceed to regular chat group selection
    setCurrentStep("chatGroup");
  };

  // Handle internal chat modal choice
  const handleInternalChatChoice = async (isInternal) => {
    console.log("handleInternalChatChoice CALLED - isInternal:", isInternal, "selectedMembers.length:", selectedMembers.length, "directMessageRefused:", directMessageRefused, "kjuupCompanies:", kjuupCompanies);
    setShowInternalChatModal(false);
    
    // NEW: Handle case when no members are selected
    if (selectedMembers.length === 0) {
      console.log("handleInternalChatChoice - No members selected, isInternal:", isInternal);
      
      if (isInternal) {
        // Fetch all Kjuup+ companies for the logged-in user
        console.log("handleInternalChatChoice - Fetching Kjuup+ companies for current user");
        try {
          setLoading(true);
          const { data } = await callAPI(`/company/my`, {}, "GET");
          
          console.log("handleInternalChatChoice - /company/my response:", data);
          
          if (data.status && data.companies && data.companies.length > 0) {
            // Filter to only Kjuup+ companies
            const kjuupPlusCompanies = data.companies.filter(c => c.isKjuup === true);
            
            console.log("handleInternalChatChoice - Found", kjuupPlusCompanies.length, "Kjuup+ companies");
            
            if (kjuupPlusCompanies.length === 1) {
              // Only one Kjuup+ company, use it directly
              setSelectedCompany(kjuupPlusCompanies[0]);
              setCurrentStep("chatGroup");
            } else if (kjuupPlusCompanies.length > 1) {
              // Multiple Kjuup+ companies, show selection
              setKjuupCompanies(kjuupPlusCompanies);
              setTimeout(() => {
                setShowCompanySelection(true);
              }, 100);
            } else {
              // No Kjuup+ companies found
              console.log("handleInternalChatChoice - No Kjuup+ companies found, proceeding without company");
              setSelectedCompany(null);
              setCurrentStep("chatGroup");
            }
          } else {
            // API returned no companies
            console.log("handleInternalChatChoice - No companies returned from API");
            setSelectedCompany(null);
            setCurrentStep("chatGroup");
          }
        } catch (error) {
          console.error("Error fetching companies:", error);
          $crud.notify({
            type: "error",
            message: strings.error_loading_companies || "Error loading companies",
          });
          setCurrentStep("chatGroup");
        } finally {
          setLoading(false);
        }
      } else {
        // External chat - proceed without company
        console.log("handleInternalChatChoice - External chat, proceeding without company");
        setSelectedCompany(null);
        setCurrentStep("chatGroup");
      }
      return;
    }
    
    // Check if this is for a single member (direct message) and user hasn't refused direct message
    // If user refused direct message earlier, skip to group chat flow
    if (selectedMembers.length === 1 && !directMessageRefused) {
      const selectedMember = members.find(m => m.id === selectedMembers[0]);
      if (selectedMember) {
        dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternal });
        if (isInternal) {
          // For internal direct message, show company selection if multiple companies
          if (kjuupCompanies.length === 1) {
            await createDirectChat(selectedMember, kjuupCompanies[0]);
          } else if (kjuupCompanies.length > 1) {
            // Multiple companies - show company selection for direct message too
            dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
            // Small delay to ensure internal modal is closed before showing company selection
            setTimeout(() => {
              setShowCompanySelection(true);
            }, 100);
            return;
          }
        } else {
          // External direct message
          await createDirectChat(selectedMember, null);
        }
      }
      return;
    }
    
    // Multi-member flow (group chat) OR single member after refusing direct message
    console.log("handleInternalChatChoice - isInternal:", isInternal, "kjuupCompanies:", kjuupCompanies?.length);
    if (isInternal) {
      if (kjuupCompanies.length === 1) {
        // Only one company, proceed with it
        console.log("handleInternalChatChoice - Single company, proceeding:", kjuupCompanies[0]);
        setSelectedCompany(kjuupCompanies[0]);
        setCurrentStep("chatGroup");
      } else if (kjuupCompanies.length > 1) {
        // Multiple companies, show selection dialog
        console.log("handleInternalChatChoice - Multiple companies, showing selection:", kjuupCompanies);
        // Small delay to ensure internal modal is closed before showing company selection
        setTimeout(() => {
          setShowCompanySelection(true);
        }, 100);
      } else {
        // No companies (shouldn't happen, but handle gracefully)
        console.log("handleInternalChatChoice - No companies found, proceeding without company");
        setCurrentStep("chatGroup");
      }
    } else {
      // User chose "No", proceed to regular chat group selection
      setSelectedCompany(null);
      setCurrentStep("chatGroup");
    }
  };

  // Handle company selection for internal chat
  const handleCompanySelected = async (company) => {
    setSelectedCompany(company);
    setShowCompanySelection(false);
    
    // Check if this is for a single member direct message (not refused)
    // or for group chat flow
    if (selectedMembers.length === 1 && !directMessageRefused) {
      // Single member, direct message with selected company (internal)
      const selectedMember = members.find(m => m.id === selectedMembers[0]);
      if (selectedMember) {
        dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: true });
        await createDirectChat(selectedMember, company);
      }
    } else {
      // Multi-member or refused direct message - go to group chat selection
      setCurrentStep("chatGroup");
    }
  };

  // Handle chat group selection continue
  const handleChatGroupContinue = async (chatGroup, isExisting = false) => {
    // Determine if this is an internal chat based on selectedCompany
    const isInternalChat = selectedCompany && selectedCompany.id ? true : false;
    // Get the room ID - API may return it as 'building', 'buildingId', or 'buildingList'
    const roomId = chatGroup.building || chatGroup.buildingId || chatGroup.buildingList;
    // Get company ID - prefer chatGroup.companyId, fall back to selectedCompany.id for internal chats
    const companyId = chatGroup.companyId || selectedCompany?.id;
    
    if (isExisting && chatGroup.id) {
      // If existing chat group selected, open it directly (skip room selection)
      try {
        // Set deeplinkData with isInternal flag FIRST so loading screen shows correct color
        dispatch({ type: SET_DEEPLINK_DATA, payload: { 
          chatGroupId: chatGroup.id, 
          isInternal: isInternalChat, 
          roomId: roomId,
          companyId: companyId 
        }});
        
        dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternalChat });
        setLoading(true);
        
        // Close the dialog immediately
        if (onComplete) onComplete();
        if (onClose) onClose();
        
        // Navigate to projects page first
        history.push("/projects");
        
        dispatch({ type: TOGGLE_CHAT, payload: false });
        dispatch({ type: RIGHT_TOP, rightTop: '' });
        dispatch({ type: TOGGLE_DRAWER, open: false });
        dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
        dispatch({ type: TOGGLE_PROJECT, payload: false });
        dispatch({ type: OPEN_PROJECTS, openProjects: [] });
        dispatch({ type: OPEN_CHATS, openChats: [] });
        dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
        dispatch({ type: SET_IN_ACTIVITY, status: false });
        dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
        dispatch({ type: GROUPS, groups: [] });
        
        // Set the correct tab: 0 for external, 1 for internal
        dispatch({ type: SET_TAB, status: isInternalChat ? 1 : 0 });
        
        // Use requestAnimationFrame for immediate execution instead of setTimeout
        requestAnimationFrame(() => {
          dispatch({ type: SET_OPEN_SIDE_BAR, payload: 'project1' });
          dispatch({ type: RIGHT_TOP, rightTop: 'project1' });
        });
      } catch (error) {
        console.error("Error opening chat group:", error);
        $crud.notify({
          type: "error",
          message: strings.error_opening_chat,
        });
      } finally {
        setLoading(false);
      }
    } else {
      // If new group chat name typed, continue to room selection
      setSelectedChatGroup(chatGroup);
      setCurrentStep("room");
    }
  };

  // Handle room selection complete
  const handleRoomComplete = async (createdChat) => {
    // Determine if this is an internal chat based on the flag passed from RoomSelection or selectedCompany
    // Ensure it's a boolean (not a UUID string)
    const isInternalChat = createdChat.isInternal === true || !!(selectedCompany && selectedCompany.id);
    // Get the room ID - API may return it as 'building' or 'buildingId'
    const roomId = createdChat.building || createdChat.buildingId;
    // Get the company ID from createdChat or selectedCompany (needed for internal chat opening in MyProject)
    const companyId = createdChat.companyId || selectedCompany?.id;
    
    
    // Set deeplinkData with isInternal flag FIRST so loading screen shows correct color
    // Include companyId for internal chats - required by MyProject to find and open the room
    dispatch({ type: SET_DEEPLINK_DATA, payload: { 
      roomId: roomId, 
      isInternal: isInternalChat,
      companyId: companyId,
      chatGroupId: createdChat.id,
    } });
    
    // Show the global loading screen immediately BEFORE closing the dialog
    // Include isInternal to ensure the loader shows correct color throughout
    dispatch({ type: SET_DEEPLINK_LOADING, loading: true, isInternal: isInternalChat });
    
    // Close the dialog
    if (onComplete) onComplete();
    if (onClose) onClose();
    
    // Navigate to projects
    history.push("/projects");
    
    // Set up the navigation with correct internal/external flag
    dispatch({ type: TOGGLE_CHAT, payload: false });
    dispatch({ type: RIGHT_TOP, rightTop: '' });
    dispatch({ type: TOGGLE_DRAWER, open: false });
    dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
    dispatch({ type: TOGGLE_PROJECT, payload: false });
    dispatch({ type: OPEN_PROJECTS, openProjects: [] });
    dispatch({ type: OPEN_CHATS, openChats: [] });
    dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
    dispatch({ type: SET_IN_ACTIVITY, status: false });
    dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
    dispatch({ type: GROUPS, groups: [] });
    // IMPORTANT: Clear groupChats to prevent old opened states from persisting
    dispatch({ type: GROUP_CHATS, groupChats: [] });
    
    // Set the correct tab: 0 for external, 1 for internal
    dispatch({ type: SET_TAB, status: isInternalChat ? 1 : 0 });
    
    // Set sidebar and right top immediately
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
    dispatch({ type: RIGHT_TOP, rightTop: "project1" });
    
    // Use a longer delay to ensure projects are loaded before setting newGroupData
    // The projects refresh (UPDATE_PROJECT_ACTION_FIRE) triggers an API call that takes time
    // We need to wait for the internal projects to be loaded so that:
    // 1. MyProject opens the room via chat_click_plus (using deeplinkData)
    // 2. ProjectDetail is rendered for the room
    // 3. ProjectDetail's newGroupData effect opens the chat group
    setTimeout(() => {
      // Use SET_NEW_GROUP_DATA to handle opening the new chat group within the room
      dispatch({ type: SET_NEW_GROUP_DATA, payload: { 
        ...createdChat, 
        building: roomId,
        id: createdChat.id,
        isInternal: isInternalChat,
        companyId: companyId,
      } });
    }, 1500); // 1.5 second delay to allow projects to load
  };

  // Get selected member objects
  const getSelectedMemberObjects = () => {
    return members.filter(m => selectedMembers.includes(m.id));
  };

  // Render based on current step
  if (currentStep === "chatGroup") {
    return (
      <div
        className="create-message-overlay"
        onClick={onClose}
      >
        <ChatGroupSelection
          key={`${selectedMembers.join('-')}-${selectedCompany?.id || 'external'}`}
          selectedMemberIds={selectedMembers}
          members={getSelectedMemberObjects()}
          company={selectedCompany}
          onBack={() => {
            setCurrentStep("members");
            setDirectMessageRefused(false); // Reset the flag when going back
          }}
          onContinue={handleChatGroupContinue}
          onComplete={onComplete}
          newOnly={selectionKind === "combined"}
        />
      </div>
    );
  }

  if (currentStep === "room") {
    return (
      <div
        className="create-message-overlay"
        onClick={onClose}
      >
        <RoomSelection
          selectedMemberIds={selectedMembers}
          members={getSelectedMemberObjects()}
          chatGroup={selectedChatGroup}
          company={selectedCompany}
          onBack={() => setCurrentStep("chatGroup")}
          onComplete={handleRoomComplete}
          teamsPeople={selectionKind === "combined" ? pickedTeams : []}
          teamsAccountId={teamsAccountId}
        />
      </div>
    );
  }

  const companyUserIds = new Set(
    companyGroups.flatMap((cg) => (cg.users || []).map((cu) => cu.id))
  );
  const visibleMembers = members.filter((m) => !companyUserIds.has(m.id));

  return (
    <div
      className="create-message-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="create-message-dialog"
      >
        {/* Header */}
        <div className="create-message-header">
          <button
            onClick={onClose}
            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
          <h5 className="create-message-header-title">
            {strings.add_member_title}
          </h5>
          <button
            onClick={() => {
              console.log("Continue button CLICKED - loading:", loading);
              handleContinue();
            }}
            disabled={loading}
            className="kj-btn kj-btn--ghost"
          >
            {strings.continue_btn}
            {!loading && <span className="material-symbols-outlined">keyboard_arrow_right</span>}
          </button>
        </div>

        {/* Search Bar */}
        <div className="create-message-search-section">
          <div className="create-message-search-wrapper">
            <FiSearch
              size={18}
              color="#999"
              className="create-message-search-icon"
            />
            <input
              ref={inputRef}
              type="text"
              autoFocus
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={strings.search_placeholder}
              className={`create-message-search-input ${inputFocused ? 'focused' : ''}`}
            />
          </div>
          {teamsTabOn ? (
            <ContactSourceTabs
              tab={contactTab}
              onTab={setContactTab}
              kjuupCount={selectedMembers.length}
              teamsCount={pickedTeams.length}
              strings={strings}
              isGerm={isGerm}
              className="member-selection__tabs"
            />
          ) : null}
        </div>

        {/* Members List */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="create-message-list"
        >
          {teamsTabOn && contactTab === "teams" ? (
            <TeamsContactsPanel
              accounts={teamsAccounts}
              accountId={teamsAccountId}
              onPickAccount={pickTeamsAccount}
              people={teamsSearch.people === null ? null : mergeTeamsPeople(teamsSearch.people, pickedTeams)}
              directory={teamsSearch.directory}
              query={searchValue}
              selected={pickedTeams}
              onToggle={toggleTeamsSelection}
              disabled={teamsBusy}
              strings={strings}
              isGerm={isGerm}
            />
          ) : loadingMembers && members.length === 0 ? (
            <div className="create-message-list-loading">
              <Oval
                height={40}
                width={40}
                color={KJUUP_COLORS.base_color}
                wrapperStyle={{}}
                visible={true}
                ariaLabel="loading"
                strokeWidth={5}
              />
            </div>
          ) : members.length === 0 ? (
            <div className="create-message-list-empty">
              <p>{strings.no_members_found}</p>
            </div>
          ) : (
            <>
              {companyGroups.length > 0 && (() => {
                const searchLower = (searchValue || "").toLowerCase().trim();
                const filtered = searchLower
                  ? companyGroups.map((cg) => ({
                      ...cg,
                      users: cg.users.filter((u) => {
                        const parts = searchLower.split(/\s+/);
                        if (parts.length > 1) {
                          const first = parts[0], rest = parts.slice(1).join(" ");
                          return (
                            ((u.firstName || "").toLowerCase().startsWith(first) && (u.lastName || "").toLowerCase().startsWith(rest)) ||
                            ((u.lastName || "").toLowerCase().startsWith(first) && (u.firstName || "").toLowerCase().startsWith(rest))
                          );
                        }
                        return (u.firstName || "").toLowerCase().startsWith(searchLower) || (u.lastName || "").toLowerCase().startsWith(searchLower);
                      }),
                    })).filter((cg) => cg.users.length > 0)
                  : companyGroups;
                if (!filtered.length) return null;
                return (
                  <div className="member-selection__companies">
                    {filtered.map((cg) => {
                      const isOpen = openCompanies.has(cg.companyId);
                      return (
                        <div key={cg.companyId} className="member-selection__company">
                          <div
                            onClick={() => {
                              setOpenCompanies((prev) => {
                                const next = new Set(prev);
                                if (next.has(cg.companyId)) next.delete(cg.companyId);
                                else next.add(cg.companyId);
                                return next;
                              });
                            }}
                            className="member-selection__company-header"
                          >
                            <span className="member-selection__company-name">
                              {cg.companyName} ({cg.users.length})
                            </span>
                            <span className="material-symbols-outlined member-selection__company-chevron">
                              {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                            </span>
                          </div>
                          {isOpen && cg.users.length > 0 && (() => {
                            const visibleUsers = cg.users.filter((u) => u.id !== currentUser?.id);
                            const allSelected = visibleUsers.length > 0 && visibleUsers.every((u) => selectedMembers.includes(u.id));
                            return (
                            <div className="member-selection__company-users">
                              <div
                                onClick={() => {
                                  const userIds = visibleUsers.map((u) => u.id);
                                  if (allSelected) {
                                    setSelectedMembers((prev) => prev.filter((id) => !userIds.includes(id)));
                                  } else {
                                    setSelectedMembers((prev) => [...new Set([...prev, ...userIds])]);
                                  }
                                }}
                                className="create-message-member-item member-selection__select-all"
                              >
                                <div className="create-message-member-info kj-flex-1">
                                  <p className="create-message-member-name member-selection__select-all-label">
                                    {allSelected ? (strings.deselect_all || "Deselect all") : (strings.select_all || "Select all")} ({visibleUsers.length})
                                  </p>
                                </div>
                                <div className="create-message-checkbox-wrapper">
                                  <div className="custom_checkbox member-selection__checkbox">
                                    <label className="role-label member-selection__check-label">
                                      <input checked={allSelected} disabled type="checkbox" value="select-all" />
                                      <span className="checkmark-label member-selection__checkmark" />
                                    </label>
                                  </div>
                                </div>
                              </div>
                              {visibleUsers
                                .sort((a, b) => ((a.lastName || "").toLowerCase()).localeCompare((b.lastName || "").toLowerCase()))
                                .map((u) => {
                                  const isSelected = selectedMembers.includes(u.id);
                                  return (
                                    <div
                                      key={`company-${cg.companyId}-${u.id}`}
                                      onClick={() => toggleMemberSelection(u.id)}
                                      className="create-message-member-item member-selection__company-user"
                                    >
                                      <div className={`create-message-member-avatar ${cg.isKjuup ? "create-message-member-avatar-kjuup" : ""}`}>
                                        {u.img ? (
                                          <img
                                            src={BASE_URL + u.img}
                                            alt={u.name}
                                            onError={(e) => { e.target.src = process.env.PUBLIC_URL + "/assets/img/profile.png"; }}
                                          />
                                        ) : (
                                          <div className="create-message-member-avatar-placeholder">
                                            {(u.firstName?.[0] || u.name?.[0] || "?").toUpperCase()}
                                          </div>
                                        )}
                                      </div>
                                      <div className="create-message-member-info">
                                        <p className="create-message-member-name">{u.name}</p>
                                      </div>
                                      <div className="create-message-checkbox-wrapper">
                                        <div className="custom_checkbox member-selection__checkbox">
                                          <label className="role-label member-selection__check-label">
                                            <input checked={isSelected} disabled type="checkbox" value={u.id} />
                                            <span className="checkmark-label member-selection__checkmark" />
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    <div className="member-selection__divider" />
                  </div>
                );
              })()}
              {visibleMembers.map((member) => {
                // Handle company as array, object, string, or use companyName field.
                // Prefer the full `companies` list so the user's company
                // connection(s) always show below the name, independent of the
                // Kjuup+ (green outline) logic.
                let companyNames = [];
                if (Array.isArray(member.companies) && member.companies.length) {
                  companyNames = member.companies.map((c) => {
                    if (typeof c === "object" && c !== null) {
                      return c.name || c.companyName || "";
                    }
                    return String(c);
                  }).filter(Boolean);
                } else if (member.companyName) {
                  // If companyName is a direct string field
                  if (typeof member.companyName === "string") {
                    companyNames = [member.companyName];
                  } else if (Array.isArray(member.companyName)) {
                    companyNames = member.companyName.filter(Boolean);
                  }
                } else if (Array.isArray(member.company)) {
                  companyNames = member.company.map((c) => {
                    if (typeof c === "object" && c !== null) {
                      return c.name || c.companyName || "";
                    }
                    return String(c);
                  }).filter(Boolean);
                } else if (member.company && typeof member.company === "object") {
                  const name = member.company.name || member.company.companyName || "";
                  if (name) companyNames = [name];
                } else if (typeof member.company === "string" && member.company) {
                  companyNames = [member.company];
                }
                const companyDisplay = companyNames.join(", ");
                return (
                <div
                  key={member.id}
                  onClick={() => toggleMemberSelection(member.id)}
                  className="create-message-member-item"
                >
                  <div className={`create-message-member-avatar ${member.isKjuup ? "create-message-member-avatar-kjuup" : ""}`}>
                    {member.avatar ? (
                      <img
                        src={BASE_URL + member.avatar}
                        alt={member.name}
                        onError={(e) => {
                          e.target.src = process.env.PUBLIC_URL + "/assets/img/profile.png";
                        }}
                      />
                    ) : (
                      <div className="create-message-member-avatar-placeholder">
                        {(member.firstName?.[0] || member.name?.[0] || "?").toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="create-message-member-info">
                    <p className="create-message-member-name">
                      {member.name || `${member.firstName} ${member.lastName}`.trim()}
                    </p>
                    {companyNames.length > 0 ? (() => {
                      const isExpanded = expandedCompanies.has(member.id);
                      const hasMultiple = companyNames.length > 1;
                      return (
                        <p
                          className={`create-message-member-company ${isExpanded ? "expanded" : ""}`}
                          title={companyDisplay}
                        >
                          <span className="create-message-member-company-text">
                            {isExpanded ? companyDisplay : companyNames[0]}
                          </span>
                          {hasMultiple ? (
                            <span
                              className="create-message-member-company-toggle member-selection__company-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCompanyExpanded(member.id);
                              }}
                            >
                              {isExpanded ? strings.show_less : strings.see_more}
                            </span>
                          ) : null}
                        </p>
                      );
                    })() : null}
                  </div>
                  <div className="create-message-checkbox-wrapper">
                    <div className="custom_checkbox member-selection__checkbox">
                      <label className="role-label member-selection__check-label">
                        <input
                          checked={selectedMembers.includes(member.id)}
                          disabled
                          type="checkbox"
                          value={member.id}
                        />
                        <span className="checkmark-label member-selection__checkmark" />
                      </label>
                    </div>
                  </div>
                </div>
              );
              })}
              {loadingMore && (
                <div className="create-message-loading-more">
                  <Oval
                    height={30}
                    width={30}
                    color={KJUUP_COLORS.base_color}
                    wrapperStyle={{}}
                    visible={true}
                    ariaLabel="loading"
                    strokeWidth={4}
                  />
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* Internal Chat Confirmation Modal */}
      {showInternalChatModal && (
        <div
          className="create-message-modal-overlay"
          onClick={() => setShowInternalChatModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="create-message-modal"
          >
            <h4 className="create-message-modal-title">
              {strings.create_internal_chat}
            </h4>
            <p className="create-message-modal-text">
              {strings.internal_chat_description}
            </p>
            <div className="create-message-modal-buttons">
              <button
                onClick={() => handleInternalChatChoice(false)}
                disabled={loading}
                className="kj-btn kj-btn--primary create-message-modal-btn"
              >
                {strings.external_projects}
              </button>
              <button
                onClick={() => handleInternalChatChoice(true)}
                disabled={loading}
                className="kj-btn kj-btn--primary kj-btn--internal create-message-modal-btn"
              >
                {strings.internal_project}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Selection Dialog for Internal Chat */}
      {console.log("Render - showCompanySelection:", showCompanySelection, "kjuupCompanies:", kjuupCompanies?.length)}
      {showCompanySelection && (
        <CompanyListDialog
          handleClose={() => {
            setShowCompanySelection(false);
            // setSelectedCompany(null);
          }}
          setParam={() => {}}
          companies={kjuupCompanies}
          handleReJoinCompany={handleCompanySelected}
          isInternal={true}
        />
      )}

      {/* Teams contacts only: start the Teams chat (group name for several people) */}
      {showTeamsChatModal && (
        <div
          className="create-message-modal-overlay"
          onClick={() => (teamsBusy ? null : setShowTeamsChatModal(false))}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="create-message-modal"
          >
            <h4 className="create-message-modal-title">
              {teamsText("teams_new_chat_title", "Neuer Teams-Chat", "New Teams chat")}
            </h4>
            {pickedTeams.length > 1 ? (
              <input
                type="text"
                className="create-message-search-input member-selection__teams-topic"
                value={teamsTopic}
                onChange={(e) => setTeamsTopic(e.target.value)}
                placeholder={teamsText("teams_new_chat_topic", "Gruppenname (optional)", "Group name (optional)")}
                maxLength={250}
                disabled={teamsBusy}
                autoFocus
              />
            ) : null}
            <div className="create-message-modal-buttons">
              <button
                onClick={() => setShowTeamsChatModal(false)}
                disabled={teamsBusy}
                className="kj-btn kj-btn--outline create-message-modal-btn"
              >
                {strings.cancel}
              </button>
              <button
                onClick={startTeamsChat}
                disabled={teamsBusy}
                className="kj-btn kj-btn--primary create-message-modal-btn"
              >
                {teamsBusy
                  ? teamsText("teams_new_chat_creating", "Chat wird erstellt…", "Creating chat…")
                  : teamsText("teams_new_chat_start", "Chat starten", "Start chat")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Message Confirmation Modal */}
      {showDirectMessageModal && (
        <div
          className="create-message-modal-overlay"
          onClick={() => setShowDirectMessageModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="create-message-modal"
          >
            <h4 className="create-message-modal-title">
              {strings.start_direct_message || "Start direct message?"}
            </h4>
            <div className="create-message-modal-buttons">
              <button
                onClick={handleDirectMessageCancel}
                disabled={loading}
                className="kj-btn kj-btn--primary create-message-modal-btn"
              >
                {strings.no}
              </button>
              <button
                onClick={handleDirectMessageConfirm}
                disabled={loading}
                className="kj-btn kj-btn--primary create-message-modal-btn"
              >
                {strings.yes}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberSelection;

